import React, { useRef, useEffect } from "react";
import { upload_file } from "../../../../assets/images/Images";
import GoogleDriveUpload from "./GoogleDriveUpload";
import type { GoogleTokenData } from "../../../../services/googleDriveService";

export interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileSelect: (files: File[]) => void;
  /** Google token data passed from parent (to avoid creating a new store) */
  googleTokenData?: GoogleTokenData | null;
  /** Auth token for API calls */
  authToken?: string;
  /** Callback to submit Google token to backend */
  onGoogleTokenSubmit?: (tokenData: { google_token: GoogleTokenData }) => void;
  /** Callback to fetch Google token from backend */
  onGoogleTokenFetch?: () => void;
  /** Callback when file IDs are selected from Google Drive (for embeddings) */
  onGoogleDriveFileIds?: (fileIds: string[]) => void;
}

interface FileUploadModalConfig {
  selectFromDriveText?: string;
  uploadFromComputerText?: string;
  acceptedFileTypes?: string;
  /** Text to show when Google Drive is not connected */
  connectToDriveText?: string;
  /** Whether to show Google Drive option */
  showGoogleDrive?: boolean;
}

const FileUploadModal: React.FC<FileUploadModalProps & FileUploadModalConfig> = ({
  isOpen,
  onClose,
  onFileSelect,
  googleTokenData,
  authToken,
  onGoogleTokenSubmit,
  onGoogleTokenFetch,
  onGoogleDriveFileIds,
  selectFromDriveText = "Select From Drive",
  uploadFromComputerText = "Upload from Computer",
  acceptedFileTypes = ".pdf,.PDF,.png,.PNG,.jpg,.JPG,.jpeg,.JPEG,.txt,.TXT,.xls,.XLS,.xlsx,.XLSX,.doc,.DOC,.docx,.DOCX",
  connectToDriveText = "Connect To Drive",
  showGoogleDrive = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      // Use setTimeout to avoid immediate trigger from the button click
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleUploadFromComputer = () => {
    fileInputRef.current?.click();
    // Don't close here - wait for file selection to complete
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      onFileSelect(fileArray);
      onClose(); // Close modal after file is selected
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handle files selected from Google Drive
   */
  const handleGoogleDriveFileSelect = (files: File[]) => {
    onFileSelect(files);
    // onClose is called by GoogleDriveUpload component
  };

  if (!isOpen) return null;

  return (
    <div ref={popupRef} className="absolute bottom-full -right-4  mb-2 z-[113]">
      {/* Popup Container */}
      <div className="p-[18px] shadow-[0px_2px_8px_#dbdbdb] rounded-[5px] bg-white flex flex-col gap-[14px]">
        {/* Select From Drive Option */}
        {showGoogleDrive && (
          <GoogleDriveUpload
            onFileSelect={handleGoogleDriveFileSelect}
            onClose={onClose}
            googleTokenData={googleTokenData}
            authToken={authToken}
            onGoogleTokenSubmit={onGoogleTokenSubmit}
            onGoogleTokenFetch={onGoogleTokenFetch}
            onFileIdsSelected={onGoogleDriveFileIds}
            connectText={connectToDriveText}
            selectText={selectFromDriveText}
          />
        )}

        {/* Upload from Computer Option */}
        <div
          className="flex items-center gap-[11px] cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleUploadFromComputer}
        >
          <div className="w-[19px] h-[21px] flex-shrink-0">
            <img src={upload_file} alt="Upload File" className="w-full h-full object-contain" />
          </div>
          <p className="text-black text-[13px] m-0 whitespace-nowrap">{uploadFromComputerText}</p>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept={acceptedFileTypes}
      />
    </div>
  );
};

export default FileUploadModal;
